import { queryClient } from '..';
import { useMutation } from '@tanstack/react-query';
import { api } from 'api';
import { AxiosError } from 'axios';

import {
    ResidentShiftNotesCreateParams,
    ResidentShiftNotesDeleteParams,
    ResidentShiftNotesReadParams,
    ResidentShiftNotesResponse,
    ResidentShiftNotesUpdateParams,
} from 'types/residentShiftNotes';

import { useAppDispatch } from 'constants/redux';
import { useErrorHandledQuery } from 'hooks/useErrorHandledQuery';
import { formatApiParams, useToken } from 'lib/common';
import { showAlert, throwError } from 'redux/actions/messages';

export const useResidentShiftNotesQuery = (jsonParams: ResidentShiftNotesReadParams) => {
    const { residentId, branchId } = jsonParams;
    const params = formatApiParams(jsonParams);
    const token = useToken();

    return useErrorHandledQuery(
        ['residentShiftNotes', residentId],
        async () => {
            const options = { headers: { authorization: token }, params };
            const { data } = await api.get('/resident-shift-notes', options);
            return data.response as ResidentShiftNotesResponse[];
        },
        { enabled: !!residentId && !!branchId, staleTime: 300000 }
    );
};

const invalidateQueries = ({ userId, residentId }: { userId: number; residentId: number }) =>
    Promise.all([
        queryClient.invalidateQueries({
            queryKey: ['residentShiftNotes', residentId],
            exact: false,
            refetchType: 'active',
        }),
        queryClient.invalidateQueries({
            queryKey: ['rewards', userId],
            exact: false,
            refetchType: 'active',
        }),
    ]);

export const useResidentShiftNotesCreateMutation = (userId: number) => {
    const dispatch = useAppDispatch();
    const token = useToken();

    return useMutation({
        mutationFn: async (jsonParams: ResidentShiftNotesCreateParams) => {
            const params = formatApiParams(jsonParams);
            const options = {
                headers: { authorization: token },
                params: { branch_id: jsonParams.branchId }, // For permission checking
            };
            await api.post('/resident-shift-notes', params, options);
        },
        onSuccess: async (_, { residentId }) => {
            await invalidateQueries({ userId, residentId });

            dispatch(
                showAlert({
                    open: true,
                    message: 'Note created successfully!',
                    alertSeverity: 'success',
                })
            );
        },
        onError: (error) => {
            const customError = error as AxiosError;
            dispatch(throwError(customError));
            throw customError;
        },
        retry: false,
    });
};

export const useResidentShiftNotesUpdateMutation = (userId: number) => {
    const dispatch = useAppDispatch();
    const token = useToken();

    return useMutation({
        mutationFn: async (jsonParams: ResidentShiftNotesUpdateParams) => {
            const { id, ...params } = formatApiParams(jsonParams) as any;
            const options = {
                headers: { authorization: token },
                params: { branch_id: jsonParams.branchId }, // For permission checking
            };
            await api.put(`/resident-shift-notes/${id}`, params, options);
        },
        onSuccess: async (_, { residentId }) => {
            await invalidateQueries({ userId, residentId });

            dispatch(
                showAlert({
                    open: true,
                    message: 'Note updated successfully!',
                    alertSeverity: 'success',
                })
            );
        },
        onError: (error) => {
            const customError = error as AxiosError;
            dispatch(throwError(customError));
            throw customError;
        },
        retry: false,
    });
};

export const useResidentShiftNotesDeleteMutation = (userId: number) => {
    const dispatch = useAppDispatch();
    const token = useToken();

    return useMutation({
        mutationFn: async (jsonParams: ResidentShiftNotesDeleteParams) => {
            const options = {
                headers: { authorization: token },
                params: { branch_id: jsonParams.branchId }, // For permission checking
            };
            await api.delete(`/resident-shift-notes/${jsonParams.id}`, options);
        },
        onSuccess: async (_, { residentId }) => {
            await invalidateQueries({ userId, residentId });

            dispatch(
                showAlert({
                    open: true,
                    message: 'Note deleted successfully!',
                    alertSeverity: 'success',
                })
            );
        },
        onError: (error) => {
            const customError = error as AxiosError;
            dispatch(throwError(customError));
            throw customError;
        },
        retry: false,
    });
};
